import { Routes } from '@/assets/configs'
import { useRouter } from 'next/router'
import { gtmPageView } from '@/helpers/GTMHelper'
import { appendSearchParams, deleteQueryParam, getFirstQueryParam } from '@/utils'
import { useIsQueryOwner } from '@/hooks/index'
import { usePetsittingMode } from '@/store/usePetsittingMode'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMobile, useMounted } from 'emt-components-v2'
import useCurrentUser from '@/hooks/useCurrentUser'

export enum ProfileKind {
  Community = 'Community',
  PetSitting = 'PetSitting',
}

const useFloatingProfile = (options: {
  defaultProfileKind: ProfileKind
  forceRedirect?: boolean
  publicProfile?: boolean
}) => {
  const { forceRedirect = false, defaultProfileKind, publicProfile = false } = options
  const router = useRouter()
  const isMounted = useMounted()
  const [isMobile] = useMobile()
  const { isPetsittingMode } = usePetsittingMode()

  const [isQueryOwner, , appendQueryType] = useIsQueryOwner()
  const { currentUnknownUser } = useCurrentUser()

  const [profileIsOpen, setProfileIsOpen] = useState<boolean>(false)
  const [profileKind, setProfileKind] = useState<ProfileKind | null>(null)

  const customerId = useMemo(() => getFirstQueryParam(router.query['customer-id']), [router.query['customer-id']])

  const sendGtmEvent = useCallback(
    (profId: string, kind: ProfileKind) => {
      switch (kind) {
        case ProfileKind.PetSitting:
          gtmPageView({
            pageUrl: appendQueryType(
              publicProfile ? Routes.PublicProfilePetSitter(profId) : Routes.ProfilePetSitter(profId),
            ),
            pageTitle: 'PetSitterProfil',
            userId: currentUnknownUser?.id,
          })
          break

        case ProfileKind.Community:
        default:
          gtmPageView({
            pageUrl: appendQueryType(publicProfile ? Routes.PublicProfile(profId) : Routes.Profile(profId)),
            pageTitle: 'Profil',
            userId: currentUnknownUser?.id,
            userRole: isQueryOwner ? 'maitre' : 'emprunteur',
          })
      }
    },
    [appendQueryType, currentUnknownUser?.id, isQueryOwner, publicProfile],
  )

  const handleOpenProfile = useCallback(
    (profileId?: string, kind: ProfileKind = defaultProfileKind) => {
      setProfileKind(kind)

      if (profileId) {
        // évite aussi un push inutile si déjà sur le même id
        if (String(customerId) === String(profileId)) {
          // juste le tracking si tu veux
          sendGtmEvent(String(profileId), kind)
          return
        }

        router
          .push(appendSearchParams(router.asPath, { 'customer-id': profileId }), undefined, {
            scroll: false,
            shallow: true,
          })
          .then(() => sendGtmEvent(String(profileId), kind))
      } else {
        if (customerId) sendGtmEvent(String(customerId), kind)
      }
    },
    [router, defaultProfileKind, sendGtmEvent, customerId],
  )

  const handleCloseProfile = useCallback(() => {
    router.replace(deleteQueryParam(router.asPath, 'customer-id'), undefined, {
      scroll: false,
      shallow: false,
    })

    setProfileKind(null)
    setProfileIsOpen(false)
  }, [router])

  // 🔗 Handle "share" profiles (comportement initial)
  useEffect(() => {
    if (!isMounted) return
    const cid = getFirstQueryParam(router.query['customer-id'])
    const routeIsSearch = router.pathname === Routes.Search._pathname || router.pathname === Routes.PetSitter.Search

    // 🪠 Forced redirect
    if (cid && forceRedirect) {
      router.replace(deleteQueryParam(router.asPath, 'customer-id'), undefined, {
        scroll: false,
        shallow: false,
      })
      router.push(
        appendQueryType(
          isPetsittingMode
            ? publicProfile
              ? Routes.PublicProfilePetSitter(cid)
              : Routes.ProfilePetSitter(cid)
            : publicProfile
            ? Routes.PublicProfile(cid)
            : Routes.Profile(cid),
        ),
      )
      return
    }

    // 📱 Mobile behavior
    if (cid && window.screen.width <= 600) {
      handleOpenProfile(undefined, defaultProfileKind)
      return
    }

    // 💻 Desktop behavior
    if (cid && routeIsSearch) {
      router.replace(deleteQueryParam(router.asPath, 'customer-id'), undefined, {
        scroll: false,
        shallow: false,
      })
      router.push(
        appendQueryType(
          isPetsittingMode
            ? publicProfile
              ? Routes.PublicProfilePetSitter(cid)
              : Routes.ProfilePetSitter(cid)
            : publicProfile
            ? Routes.PublicProfile(cid)
            : Routes.Profile(cid),
        ),
      )
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // sync open/close with URL
  useEffect(() => {
    const shouldBeOpen = Boolean(customerId)

    setProfileIsOpen((prev) => (prev === shouldBeOpen ? prev : shouldBeOpen))

    if (!customerId) {
      setProfileKind(null)
      return
    }

    setProfileKind((prev) => prev ?? defaultProfileKind)
  }, [customerId, defaultProfileKind])

  return {
    // state
    isMobile,
    customerId: customerId || undefined,
    profileIsOpen,
    profileKind,

    // actions
    handleOpenProfile,
    handleCloseProfile,
  } as const
}

export default useFloatingProfile
